import React, { createContext, useContext, useEffect, useState } from 'react';
import Papa, { ParseResult } from 'papaparse';
import { Product, products as localProducts, slugify } from '../data/products';

interface ProductContextType {
  products: Product[];
  loading: boolean;
  error: string | null;
  refreshProducts: () => Promise<void>;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};

export const ProductProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>(localProducts);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = async () => {
    const apiUrl = process.env.REACT_APP_GOOGLE_SHEETS_URL;
    
    // If no URL is set, fall back to local data immediately
    if (!apiUrl) {
      console.log('No API URL configured, using local data');
      setProducts(localProducts);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      
      const csvText = await response.text();
      
      const convertGoogleDriveLink = (url: string) => {
        if (!url) return '';
        if (url.includes('drive.google.com')) {
          const idMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
          if (idMatch && idMatch[1]) {
            return `https://drive.google.com/thumbnail?id=${idMatch[1]}&sz=w1000`;
          }
        }
        return url;
      };
      
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        complete: (results: ParseResult<any>) => {
          const validProducts = results.data.map((row: any) => {
             // Debug log for first item
             if (row.id === '1') {
                console.log('Row 1 Image 1 Raw:', row.image1);
                console.log('Row 1 Image 1 Converted:', convertGoogleDriveLink(row.image1));
             }

             const img1 = convertGoogleDriveLink(row.image1);
             const img2 = convertGoogleDriveLink(row.image2);
             const img3 = convertGoogleDriveLink(row.image3);
             const img4 = convertGoogleDriveLink(row.image4);
             const img5 = convertGoogleDriveLink(row.image5);
             const legacyImg = convertGoogleDriveLink(row.image);

             return {
               id: Number(row.id),
               name: row.name,
               slug: (row.slug && String(row.slug).trim()) ? String(row.slug).trim().toLowerCase().replace(/\s+/g, '-') : slugify(row.name || ''),
               price: Number(row.price),
               originalPrice: Number(row.originalPrice),
               rating: Number(row.rating),
               reviews: Number(row.reviews),
               image: legacyImg || img1, // Fallback to image1 if legacy image column is empty
               images: [img1, img2, img3, img4, img5].filter(Boolean),
               tag: row.tag,
               description: row.description,
               longDescription: row.longDescription,
               usage: {
                  title: row.usageTitle,
                  steps: row.usageSteps ? row.usageSteps.split('|') : []
               },

             faqs: row.faqs ? row.faqs.split('|').map((item: string) => {
                const parts = item.split(':');
                return { question: parts[0], answer: parts.slice(1).join(':') };
             }) : []
          }; // End return object
        }); // End map callback
        
        const filteredProducts = validProducts.filter((p: any) => !isNaN(p.id));

          if (filteredProducts.length > 0) {
             setProducts(filteredProducts);
             setError(null);
          } else {
             console.warn('Parsed CSV was empty, using backup');
             setProducts(localProducts);
          }
          setLoading(false);
        },
        error: (err: any) => {
           console.error('CSV Parsing Error:', err);
           setProducts(localProducts);
           setLoading(false);
        }
      });

    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load latest products. Showing offline data.');
      setProducts(localProducts);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  return (
    <ProductContext.Provider value={{ products, loading, error, refreshProducts: fetchProducts }}>
      {children}
    </ProductContext.Provider>
  );
};
